<template>
  <div class="w-full bg-white flex flex-col pb-[12.5px]">
    <div
      class="w-[60px] min-h-1 max-h-1 bg-gray-800/20 rounded-xs self-center mt-2.5 mb-1.5"
    />

    <template v-if="props.items.length">
      <TallListItem icon="Move" @click="handleMoveClick"> Move </TallListItem>

      <template v-if="areSelectedItemsFiles">
        <TallListItem icon="Lock" @click="handlePrivacyControlsClick">
          {{ privacyControlsCopy }}
        </TallListItem>

        <TallListItem class="text-red-600" @click="handleArchiveClick">
          <Icon name="Archive" :size="20" class="mr-5 stroke-red-600" />
          Archive
        </TallListItem>
      </template>
    </template>

    <TallListItem icon="Archive" @click="handleViewArchiveClick">
      View archived copyrights
    </TallListItem>
  </div>
</template>

<script setup lang="ts">
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useRouter } from 'vue-router'
import type { Item } from '@/shared/graphql/router/graphql'
import { isFile } from '@/features/explorer/utils/itemGuards'
import { TallListItem } from '@/shared/components/Menu'
import { computed } from 'vue'
import { Icon } from '@dacr/ui'

const props = defineProps<{
  items: Item[]
}>()

const router = useRouter()
const { close, open } = useDialog()

const areSelectedItemsFiles = computed(() => props.items.every(isFile))

const selectedFiles = computed(() => props.items.filter(isFile))

const privacyControlsCopy = computed(() => {
  const allFilesPublic =
    selectedFiles.value.every((file) => !file.settings?.isPrivate) &&
    props.items.length > 1
  const allFilesPrivate =
    selectedFiles.value.every((file) => file.settings?.isPrivate) &&
    props.items.length > 1

  if (allFilesPublic) return 'Make files private'
  if (allFilesPrivate) return 'Make files public'
  return 'Privacy controls'
})

const handleArchiveClick = () => {
  open(Dialogs.ArchiveFiles, {
    props: {
      files: selectedFiles.value,
    },
  })
}

const handlePrivacyControlsClick = () => {
  if (props.items.length === 1 && isFile(props.items[0])) {
    return open(Dialogs.FilePrivacySettings, {
      props: { file: props.items[0] },
    })
  }

  const allFilesPublic = selectedFiles.value.every(
    (file) => !file.settings?.isPrivate
  )
  const allFilesPrivate = selectedFiles.value.every(
    (file) => file.settings?.isPrivate
  )

  if (allFilesPublic) {
    return open(Dialogs.MakeFilesPrivate, {
      props: { ids: selectedFiles.value.map((file) => file.id) },
    })
  }

  if (allFilesPrivate) {
    return open(Dialogs.MakeFilesPublic, {
      props: { ids: selectedFiles.value.map((file) => file.id) },
    })
  }

  open(Dialogs.UpdateFilePrivacy, {
    props: { ids: selectedFiles.value.map((file) => file.id) },
  })
}

const handleViewArchiveClick = () => {
  router.push('/archive')
  close()
}

const handleMoveClick = () => {
  open(Dialogs.MoveItems, {
    props: { ids: props.items.map((item) => item.id) },
  })
}
</script>
